import { ip_back } from "../config";

function authHeaders() {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: token } : {}),
  };
}

/** Human-readable remaining time from job `eta_seconds` (empty if unknown). */
export function formatReencodeEta(etaSeconds) {
  const s = Math.max(0, Math.round(Number(etaSeconds) || 0));
  if (!Number.isFinite(s) || s <= 0) return "";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

export function reencodeJobProgressPct(job) {
  return Math.max(0, Math.min(100, Math.round((Number(job?.progress) || 0) * 100)));
}

async function pollReencodeJob(jobId, { onProgress, signal, maxAttempts = 600 } = {}) {
  for (let i = 0; i < maxAttempts; i += 1) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const resp = await fetch(
      `${ip_back}api/mobileAPI/reencodeEvaluationVideoJob?job_id=${encodeURIComponent(jobId)}`,
      { headers: authHeaders(), signal }
    );
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(json.message || "Failed to poll re-encode job");
    }
    const data = json.data || {};
    onProgress?.(data);
    if (data.status === "complete") return data;
    if (data.status === "failed") {
      throw new Error(data.error || "Re-encode failed");
    }
    await new Promise((r) => setTimeout(r, 1500));
  }
  throw new Error("Re-encode timed out");
}

/**
 * Bake rotation/flip into evaluation videos for one exercise or a whole session.
 * @param {{ teId?: string, tsId?: string, onProgress?: Function, signal?: AbortSignal }} opts
 */
export async function bakeEvaluationVideoOrients({
  teId = null,
  tsId = null,
  onProgress,
  signal,
} = {}) {
  const body = {};
  if (teId) body.te_id = teId;
  if (tsId) body.ts_id = tsId;

  const resp = await fetch(`${ip_back}api/mobileAPI/bakeEvaluationVideoOrients`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
    signal,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || (json.status !== "success" && json.status !== true)) {
    throw new Error(json.message || "Failed to start orientation bake");
  }

  const jobId = json.data?.job_id;
  const total = Number(json.data?.total) || 0;
  if (!jobId) {
    return { total: 0, succeeded: 0, failed: 0, results: [] };
  }

  const finished = await pollReencodeJob(jobId, { onProgress, signal });
  return finished.data || { total, succeeded: total, failed: 0, results: [] };
}

/**
 * Bake transforms for a single feedback message (uses stored view_setup if body empty).
 */
export async function reencodeEvaluationVideo(messageId, transforms = {}, opts = {}) {
  const resp = await fetch(`${ip_back}api/mobileAPI/reencodeEvaluationVideo`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      message_id: messageId,
      ...transforms,
    }),
    signal: opts.signal,
  });
  const json = await resp.json().catch(() => ({}));
  if (resp.status !== 202 && !resp.ok) {
    throw new Error(json.message || "Failed to start re-encode");
  }
  const jobId = json.data?.job_id;
  if (!jobId) throw new Error("No re-encode job id");
  return pollReencodeJob(jobId, opts);
}

export default {
  bakeEvaluationVideoOrients,
  reencodeEvaluationVideo,
  formatReencodeEta,
  reencodeJobProgressPct,
};
